/**
 * Grounded chat.
 *
 * An answer is a list of *clauses*, each one labelled with how it stands: `grounded`
 * carries citation markers, `gap` says the corpus was searched and came back empty,
 * `not_asked` says the question did not cover it. The labels are not decoration — they
 * are the difference between "we found nothing" and "we did not look", which in an
 * investigation is the difference between a conclusion and a mistake.
 *
 * Markers are buttons because they always do something: they fill the evidence panel.
 * A citation you cannot follow is a footnote, and a footnote is not evidence.
 */

import { useState, type FormEvent } from "react";
import { api, type Answer, type ClauseKind } from "../lib/api";
import { useEvidence } from "../state/evidence";

/**
 * What a clause is labelled, or nothing where the prose speaks for itself.
 *
 * A total map rather than a conditional, so adding a kind to the union makes the
 * compiler ask what it is called instead of letting it inherit whichever branch a
 * ternary happened to end on.
 *
 * `grounded` carries its own citation markers and needs no caption. `conversational`
 * is a turn that never consulted the corpus, so there is nothing to caption *about*
 * the corpus — labelling it at all would be the original bug in a politer font.
 */
const CLAUSE_LABELS: Record<ClauseKind, string> = {
  grounded: "",
  derived: "computed across the corpus",
  gap: "not in the corpus",
  not_asked: "outside the question",
  conversational: "",
};

interface Turn {
  question: string;
  answer: Answer | null;
  error: string | null;
}

interface Props {
  datasetId: string;
  /** Hand a chat message to the artifact composer, so a table can cite its origin. */
  onBuildArtifact(messageId: string, question: string): void;
}

export function Chat({ datasetId, onBuildArtifact }: Props) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [chatId, setChatId] = useState<string | null>(null);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);

  async function ask(event: FormEvent) {
    event.preventDefault();
    const asked = question.trim();
    if (!asked || busy) return;
    setQuestion("");
    setBusy(true);
    setTurns((current) => [...current, { question: asked, answer: null, error: null }]);
    try {
      const answer = await api.ask(datasetId, asked, chatId);
      setChatId(answer.chat_id);
      setTurns((current) =>
        current.map((turn, index) => (index === current.length - 1 ? { ...turn, answer } : turn)),
      );
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : String(cause);
      setTurns((current) =>
        current.map((turn, index) =>
          index === current.length - 1 ? { ...turn, error: message } : turn,
        ),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="chat">
      <div className="chat-scroll" data-testid="chat-log">
        {turns.length === 0 && (
          <p style={{ color: "var(--ink-faint)", maxWidth: "48ch" }} data-testid="chat-empty">
            Ask about the corpus. Every sentence comes back marked with where it stands:
            grounded and cited, a gap the corpus does not cover, or outside what you
            asked.
          </p>
        )}

        {turns.map((turn, index) => (
          <article key={index} className="turn">
            <p className="question" data-testid="chat-question">
              {turn.question}
            </p>

            {!turn.answer && !turn.error && <p data-testid="chat-pending">thinking…</p>}

            {turn.error && (
              <p data-testid="chat-error" style={{ color: "var(--danger)" }}>
                {turn.error}
              </p>
            )}

            {turn.answer && (
              <AnswerBody
                answer={turn.answer}
                onBuildArtifact={() => onBuildArtifact(turn.answer!.message_id, turn.question)}
              />
            )}
          </article>
        ))}
      </div>

      <form onSubmit={ask} className="ask" data-testid="chat-form">
        <textarea
          aria-label="Ask a question"
          placeholder="Ask about this dataset"
          rows={2}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void ask(e);
            }
          }}
        />
        <button className="primary" type="submit" disabled={busy}>
          {busy ? "…" : "Ask"}
        </button>
      </form>
    </div>
  );
}

function AnswerBody({
  answer,
  onBuildArtifact,
}: {
  answer: Answer;
  onBuildArtifact(): void;
}) {
  const { select, selected } = useEvidence();
  const byMarker = new Map(answer.citations.map((c) => [c.marker, c]));
  // Every clause, not some: the router decides per turn, so a mixed answer would mean
  // the server broke its own contract. Reading it as "all" rather than "any" means the
  // evidence furniture stays on if that ever happens.
  const conversational =
    answer.clauses.length > 0 && answer.clauses.every((c) => c.kind === "conversational");

  return (
    <div
      className={`answer${conversational ? " conversational" : ""}`}
      data-testid="chat-answer"
      data-conversational={conversational}
    >
      {answer.clauses.map((clause, index) => (
        <p
          key={index}
          className={`clause ${clause.kind}`}
          data-kind={clause.kind}
          data-testid="clause"
        >
          {CLAUSE_LABELS[clause.kind] && (
            <span className="clause-label" data-testid="clause-label">
              {CLAUSE_LABELS[clause.kind]}
            </span>
          )}
          {clause.text}{" "}
          {clause.evidence.map((marker) => {
            const citation = byMarker.get(marker);
            if (!citation) return null;
            const isSelected =
              selected?.blockId === citation.block_id &&
              selected.spans[0]?.start_cp === citation.start_cp;
            return (
              <button
                key={marker}
                className="cite"
                data-testid="cite"
                aria-current={isSelected}
                onClick={() =>
                  select({
                    blockId: citation.block_id,
                    spans: [
                      {
                        start_cp: citation.start_cp,
                        end_cp: citation.end_cp,
                        quote: citation.quote,
                      },
                    ],
                    label: marker,
                    origin: "chat",
                  })
                }
              >
                {marker}
              </button>
            );
          })}
        </p>
      ))}

      <footer className="answer-meta mono">
        <span data-testid="answer-model">{answer.model}</span>
        {answer.expanded_terms.length > 0 && (
          <span data-testid="expanded-terms"> · expanded: {answer.expanded_terms.join(", ")}</span>
        )}
        {answer.retries > 0 && <span> · {answer.retries} retried</span>}{" "}
        {/* A turn that never read the corpus has nothing to build a table out of. The
            attribution stays — every line this tool speaks says which model spoke it —
            but offering to tabulate a greeting is how the button taught people it does
            nothing. */}
        {!conversational && (
          <button data-testid="to-artifact" onClick={onBuildArtifact}>
            Build a table from this
          </button>
        )}
      </footer>
    </div>
  );
}
